import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Check, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-EC', {
    day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export default function OperadorLogisticoPanel() {
  const [snapshots, setSnapshots] = useState([]);
  const [estados, setEstados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTexto, setFiltroTexto] = useState('');
  const [soloConCambios, setSoloConCambios] = useState(true);
  const [expandido, setExpandido] = useState(null);
  const [estadoElegido, setEstadoElegido] = useState({});
  const [aplicando, setAplicando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);
  const [mostrarMapeos, setMostrarMapeos] = useState(false);

  const cargar = () => {
    setCargando(true);
    api.get('/estados-operador/')
      .then((res) => setSnapshots(res.data.results || res.data))
      .catch(() => toast.error('No se pudo cargar la sincronización con el operador.'))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    cargar();
    api.get('/estados/').then((res) => setEstados(res.data.results || res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setFiltroTexto(busqueda.trim().toLowerCase()), 350);
    return () => clearTimeout(timer);
  }, [busqueda]);

  useEffect(() => setPagina(1), [filtroTexto, soloConCambios]);

  const filtrados = useMemo(() => {
    let lista = snapshots;
    if (soloConCambios) lista = lista.filter((s) => s.cambio_sin_revisar);
    if (filtroTexto) {
      lista = lista.filter((s) => {
        const nombre = (s.cliente_nombre || '').toLowerCase();
        const casillero = (s.codigo_casillero || '').toLowerCase();
        const guia = (s.guia || '').toLowerCase();
        const tracking = (s.tracking || '').toLowerCase();
        return nombre.includes(filtroTexto) || casillero.includes(filtroTexto)
          || guia.includes(filtroTexto) || tracking.includes(filtroTexto);
      });
    }
    return lista;
  }, [snapshots, soloConCambios, filtroTexto]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const paginados = filtrados.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

  const resumen = {
    total: snapshots.length,
    cambios: snapshots.filter((s) => s.cambio_sin_revisar).length,
    noEncontrados: snapshots.filter((s) => !s.encontrado).length,
  };

  const toggleExpandir = (snap) => {
    if (expandido === snap.id) {
      setExpandido(null);
    } else {
      setExpandido(snap.id);
      setEstadoElegido((prev) => ({
        ...prev,
        [snap.id]: prev[snap.id] ?? (snap.mapeo_sugerido?.id || ''),
      }));
    }
  };

  const marcarRevisado = async (snap) => {
    try {
      const res = await api.post(`/estados-operador/${snap.id}/marcar_revisado/`);
      setSnapshots((prev) => prev.map((s) => (s.id === snap.id ? res.data : s)));
      toast.success('Marcado como revisado.');
    } catch {
      toast.error('No se pudo marcar como revisado.');
    }
  };

  const aplicarEstado = async (snap) => {
    const estadoId = estadoElegido[snap.id];
    if (!estadoId) {
      toast.error('Elige un estado para aplicar.');
      return;
    }
    setAplicando(true);
    try {
      await api.post(`/paquetes/${snap.paquete}/cambiar_estado/`, {
        estado_id: estadoId,
        motivo: `Sincronización con operador logístico: "${snap.estado_operador_raw}"`,
      });
      const res = await api.post(`/estados-operador/${snap.id}/marcar_revisado/`);
      setSnapshots((prev) => prev.map((s) => (s.id === snap.id ? res.data : s)));
      toast.success('Estado aplicado al paquete.');
      setExpandido(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aplicar el estado.');
    } finally {
      setAplicando(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Paquetes sincronizados</div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>{resumen.total}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Con cambio sin revisar</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#E65100' }}>{resumen.cambios}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">No encontrados en Helgasys</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#9E9E9E' }}>{resumen.noEncontrados}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>Operador Logístico</h2>
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={soloConCambios} onChange={(e) => setSoloConCambios(e.target.checked)} />
            Solo con cambios sin revisar
          </label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
            <input
              type="text" placeholder="Buscar cliente, casillero, guía..."
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              style={{ minWidth: '220px', paddingLeft: '32px' }}
            />
          </div>
          <button className="btn-secondary" onClick={() => setMostrarMapeos(true)} style={{ whiteSpace: 'nowrap' }}>
            <Settings size={14} /> Mapeos de estados
          </button>
          <button className="btn-secondary" onClick={cargar} style={{ whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </div>

      <p className="text-sm text-secondary" style={{ marginTop: '-8px', marginBottom: 'var(--spacing-4)' }}>
        Esta lista se llena automáticamente consultando el portal de Helgasys (3 veces al día). No cambia el estado que ve el cliente — tú decides si aplicar cada cambio.
      </p>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ width: '36px' }}></th>
              <th>Casillero</th>
              <th>Cliente</th>
              <th>Guía / Tracking</th>
              <th>Estado Ecuafly</th>
              <th>Estado Helgasys</th>
              <th>Última consulta</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={8} className="text-secondary">Cargando...</td></tr>
            ) : filtrados.length === 0 ? (
              <tr><td colSpan={8} className="text-secondary">
                {soloConCambios ? 'No hay cambios sin revisar.' : 'No hay datos de sincronización todavía.'}
              </td></tr>
            ) : paginados.map((snap) => {
              const isOpen = expandido === snap.id;
              return (
                <React.Fragment key={snap.id}>
                  <tr
                    style={{ cursor: 'pointer', background: isOpen ? 'var(--color-bg-secondary)' : (snap.cambio_sin_revisar ? '#FFF8E1' : undefined) }}
                    onClick={() => toggleExpandir(snap)}
                  >
                    <td>{isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                    <td className="mono">{snap.codigo_casillero || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{snap.cliente_nombre}</td>
                    <td className="text-sm mono">{snap.guia || snap.tracking || '—'}</td>
                    <td className="text-sm">{snap.estado_actual_nombre || '—'}</td>
                    <td className="text-sm">
                      {!snap.encontrado ? (
                        <span className="text-secondary">No encontrado</span>
                      ) : (
                        <span style={{ fontWeight: snap.cambio_sin_revisar ? 700 : 400 }}>
                          {snap.estado_operador_raw || '—'}
                        </span>
                      )}
                      {snap.mapeo_sugerido && (
                        <div className="text-xs text-secondary">→ sugerido: {snap.mapeo_sugerido.nombre}</div>
                      )}
                    </td>
                    <td className="text-sm text-secondary" style={{ whiteSpace: 'nowrap' }}>{formatFecha(snap.ultima_consulta)}</td>
                    <td>
                      {snap.cambio_sin_revisar && (
                        <button
                          className="btn-ghost btn-sm"
                          onClick={(e) => { e.stopPropagation(); marcarRevisado(snap); }}
                          title="Marcar como revisado sin aplicar cambios"
                        >
                          <Check size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={8} style={{ padding: 'var(--spacing-4)', background: 'var(--color-bg-secondary)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--spacing-4)' }}>
                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Historial reportado por Helgasys</h4>
                            {(snap.seguimiento_json || []).length === 0 ? (
                              <p className="text-sm text-secondary">Sin historial disponible.</p>
                            ) : (
                              <div style={{ display: 'grid', gap: '6px', maxHeight: '220px', overflowY: 'auto' }}>
                                {snap.seguimiento_json.map((ev, i) => (
                                  <div key={i} className="card" style={{ padding: '8px 10px' }}>
                                    <div style={{ fontWeight: 600, fontSize: '12px' }}>{ev.estado}</div>
                                    <div className="text-xs text-secondary">{ev.lugar} · {ev.fecha}</div>
                                    {ev.observacion && <div className="text-xs text-secondary">{ev.observacion}</div>}
                                  </div>
                                ))}
                              </div>
                            )}
                            {snap.guia_detectada && !snap.guia && (
                              <p className="text-sm" style={{ marginTop: 'var(--spacing-3)' }}>
                                Helgasys ya asignó la guía <strong className="mono">{snap.guia_detectada}</strong>, pero todavía no la registras en este paquete.
                              </p>
                            )}
                          </div>

                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Aplicar al paquete</h4>
                            <p className="text-sm text-secondary" style={{ marginBottom: 'var(--spacing-2)' }}>
                              Elige el estado interno que corresponde a "<strong>{snap.estado_operador_raw}</strong>" y aplícalo. Esto notificará al cliente si el estado tiene notificación configurada.
                            </p>
                            <select
                              value={estadoElegido[snap.id] || ''}
                              onChange={(e) => setEstadoElegido((prev) => ({ ...prev, [snap.id]: e.target.value }))}
                              style={{ marginBottom: 'var(--spacing-3)', width: '100%' }}
                            >
                              <option value="">Selecciona un estado...</option>
                              {estados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                            </select>
                            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                              <button className="btn-primary" onClick={() => aplicarEstado(snap)} disabled={aplicando} style={{ flex: 1 }}>
                                {aplicando ? 'Aplicando...' : 'Aplicar estado'}
                              </button>
                              <button className="btn-secondary" onClick={() => marcarRevisado(snap)}>
                                Ignorar cambio
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtrados.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-4)', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">Mostrar</span>
            <select value={porPagina} onChange={(e) => setPorPagina(Number(e.target.value))} style={{ width: 'auto', minWidth: '70px' }}>
              {[10, 20, 30, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-sm text-secondary">por página</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">
              {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <button className="btn-ghost btn-sm" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}>
              <ChevronLeft size={16} />
            </button>
            <button className="btn-ghost btn-sm" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {mostrarMapeos && (
        <MapeosEstadosModal estados={estados} onClose={() => setMostrarMapeos(false)} />
      )}
    </div>
  );
}

function MapeosEstadosModal({ estados, onClose }) {
  const [mapeos, setMapeos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardandoId, setGuardandoId] = useState(null);

  useEffect(() => {
    api.get('/mapeos-estados-operador/')
      .then((res) => setMapeos(res.data.results || res.data))
      .catch(() => toast.error('No se pudieron cargar los mapeos.'))
      .finally(() => setCargando(false));
  }, []);

  const guardar = async (mapeo, estadoId) => {
    setGuardandoId(mapeo.id);
    try {
      const res = await api.patch(`/mapeos-estados-operador/${mapeo.id}/`, {
        estado_equivalente: estadoId || null,
      });
      setMapeos((prev) => prev.map((m) => (m.id === mapeo.id ? res.data : m)));
      toast.success('Mapeo actualizado.');
    } catch {
      toast.error('No se pudo guardar el mapeo.');
    } finally {
      setGuardandoId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '600px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Mapeos de estados del operador</h3>
        <p className="text-sm text-secondary">
          Cada vez que Helgasys reporta un texto de estado nuevo, aparece aquí sin mapeo. Asigna el estado interno equivalente (o déjalo sin mapear si no quieres reflejarlo al cliente).
        </p>
        <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: 'var(--spacing-3)' }}>
          {cargando ? (
            <p className="text-secondary">Cargando...</p>
          ) : mapeos.length === 0 ? (
            <p className="text-secondary">Todavía no hay estados de Helgasys registrados.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Estado de Helgasys</th>
                  <th>Equivalente en Ecuafly</th>
                </tr>
              </thead>
              <tbody>
                {mapeos.map((m) => (
                  <tr key={m.id}>
                    <td className="text-sm">{m.codigo_raw}</td>
                    <td>
                      <select
                        value={m.estado_equivalente || ''}
                        disabled={guardandoId === m.id}
                        onChange={(e) => guardar(m, e.target.value)}
                        style={{ fontSize: '12px' }}
                      >
                        <option value="">Sin mapear</option>
                        {estados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacing-4)' }}>
          <button className="btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
